import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { AzavistaApiService, IGetReportRequest, IGetReportResponse } from '@azavista/servicelib';
import { AbstractWidgetDirective, IWidgetComponentDefinition } from '@azavista/components/widgets';
import { AzavistaSharedService, IField, IFilter } from '@azavista/components/shared';
import { AzavistaFilterCriteriaComponent, IFilterCriteriaData } from '@azavista/components/filter-criteria';
import { EventsByTypeWidgetService } from './events-by-type-widget.service';
import { SharedService, TranslationKey } from '../../../shared';
import { IEventGroupBySettings, IEventGroupByPayload } from './interfaces';
import { IWidgetDataChanges, WidgetViewDetailsNavigationPaths } from '../interfaces';
import { WidgetsUtilsService } from '../widgets-utils.service';
import { ChartType } from '../apextypes';

@Component({
    templateUrl: 'events-by-type-widget.component.html',
    styleUrls: ['events-by-type-widget.component.css']
})
export class EventsByTypeWidgetComponent extends AbstractWidgetDirective implements OnInit, OnDestroy {
    chart: ApexChart = {
        type: 'donut'
    };
    series: ApexAxisChartSeries | ApexNonAxisChartSeries;
    title: ApexTitleSubtitle;
    labels: string[];
    globalEventFields: IField[];
    enumTypeGlobalEventFields: IField[];
    definitionSettings: IEventGroupBySettings;

    private destroyed$ = new Subject<void>();

    constructor(
        private apiSvc: AzavistaApiService,
        private azavistaSharedSvc: AzavistaSharedService,
        private eventsByTypeWidgetSvc: EventsByTypeWidgetService,
        private matDialog: MatDialog,
        private sharedSvc: SharedService,
        private widgetsUtilsSvc: WidgetsUtilsService
    ) {
        super();
    }

    async ngOnInit() {
        this.definition.data = null;
        this.definitionSettings = this.definition.settings as IEventGroupBySettings;
        const hasRemoteSettings = !this.eventsByTypeWidgetSvc.areRemoteSettingsEmpty(this.definitionSettings.remote);
        if (hasRemoteSettings) {
            // Start loading on next loop because it causes loading output to be emitted which could cause
            // Angular change detection errors in the consumer (the dashboard component) if it changes template bound variables
            // in the event handler
            window.setTimeout(() => this.loadWidgetData(this.definition));
        } else {
            // Notify parent that we need remote settings
            this.settingsNeeded.next();
        }
        super.subscribeToDataChanges({
            next: (newData?: IWidgetDataChanges) => {
                this.dataChanged(newData);
            }
        });
        this.setCustomWidgetParams();
        this.chart = {
            type: this.definitionSettings && this.definitionSettings.local.chartType
                ? this.definitionSettings.local.chartType
                : this.chart.type,
            toolbar: { show: false },
            zoom: { enabled: false },
            height: 200,
            animations: { enabled: true },
        };
        // Determine whether the widget needs global event fields (only edit mode needs it)
        if (this.configMode) {
            this.setDefaultRemoteSettingsIfMissing();
            this.enumTypeGlobalEventFields = await this.getEnumTypeGlobalEventFields();
            const settingsAreVaid = this.eventsByTypeWidgetSvc.areSettingsValid(this.definitionSettings);
            this.settingsChanged.next({
                definition: this.definition,
                settings: this.definitionSettings,
                settingsAreValid: settingsAreVaid
            });
        }
        // this.settings.local.groupByFields = this.enumTypeGlobalEventFields.map(x => ({
        //     id: x.id,
        //     label: x.label
        // }));
        if (!this.definitionSettings.local.chartType) {
            this.definitionSettings.local.chartType = this.chart.type as any;
        }
        const { series, labels } = this.widgetsUtilsSvc.convertDataToSeriesAndLabels(this.definition.data);
        this.series = this.eventsByTypeWidgetSvc.convertSeries(this.chart.type, series);
        this.labels = labels;
    }

    setCustomWidgetParams(): void {
        this.definition.viewButtonText = this.sharedSvc.translate(TranslationKey.goToEventsList);
        this.definition.viewDetailsNavPath = WidgetViewDetailsNavigationPaths.eventsList;
        this.onSettingsChanged();
    }

    setDefaultRemoteSettingsIfMissing(): void {
        if (!this.definitionSettings.remote) {
            this.definitionSettings.remote = { event_filter: {}, field_name: '' };
        }
        if (!this.definitionSettings.remote.event_filter) {
            this.definitionSettings.remote.event_filter = {};
        }
    }

    loadWidgetData(definition: IWidgetComponentDefinition): void {
        this.definitionSettings = definition.settings;
        if (definition.size?.height) {
            this.chart.height = definition.size.height;
            this.chart.width = definition.size.width;
        }
        const req: IGetReportRequest = {
            reportType: definition.reportType,
            widgetId: definition.id,
            payload: {
                event_filter: this.definitionSettings.remote.event_filter,
                field_name: this.definitionSettings.remote.field_name
            } as IEventGroupByPayload
        };
        this.notifyLoading(true);
        this.apiSvc.getReport(req)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: response => this.processDataLoaded(definition, response),
                error: () => this.processDataLoadedError()
            });
    }

    processDataLoaded(definition: IWidgetComponentDefinition, response: IGetReportResponse): void {
        this.notifyLoading(this.sharedSvc.getLoadingOnReportLoaded(response));
        definition.data = response.data;
        definition.dataChangedSubject.next({ data: definition.data });
    }

    processDataLoadedError(): void {
        // TODO: What if report data fails ?
        this.notifyLoading(false);
    }

    onShowFilter(): void {
        const dialogData: IFilterCriteriaData = {
            allFilterNames: [],
            allFields: this.globalEventFields,
            filter: { id: 'dummy-id', name: 'dummy-name', criteria: [] },
            isNew: true, hideFilterName: true,
            searchParams: this.definitionSettings.remote.event_filter || {}
        } as IFilterCriteriaData;
        const dialogRef = this.matDialog.open(AzavistaFilterCriteriaComponent, {
            data: dialogData,
            panelClass: 'filter-criteria-dialog',
        });
        const deleteSubscription = dialogRef.componentInstance.deleted.subscribe({
            next: () => {
                deleteSubscription.unsubscribe();
                this.definitionSettings.remote.event_filter = {};
                dialogRef.close();
            }
        });
        dialogRef.afterClosed().subscribe({
            next: dialogResult => {
                deleteSubscription.unsubscribe();
                this.processEditFilterDialogResult(dialogResult);
            }
        });

    }

    processEditFilterDialogResult(dialogResult: IFilter): void {
        if (!dialogResult) {
            return;
        }
        const searchParams = this.sharedSvc.createSearchParams(dialogResult.criteria);
        this.definitionSettings.remote.event_filter = searchParams;
    }

    chartTypeChanged(newType: string): void {
        this.chart.type = newType as ChartType;
        const { series } = this.widgetsUtilsSvc.convertDataToSeriesAndLabels(this.definition.data);
        this.series = this.eventsByTypeWidgetSvc.convertSeries(this.chart.type, series);
    }

    dataChanged(newData?: IWidgetDataChanges): void {
        let chartTypeChanged = false;
        let shouldConvertSeries = false;

        if (newData.settings && newData.settings.local) {
            if (this.chart.type !== newData.settings.local.chartType) {
                this.chart.type = newData.settings.local.chartType;
                chartTypeChanged = true;
            }
        }
        if (newData.data) {
            shouldConvertSeries = true;
        }
        if (newData.width && this.chart.width !== newData.width) {
            this.chart.width = newData.width;
        }
        if (newData.height && this.chart.height !== newData.height) {
            this.chart.height = newData.height;
        }
        if (chartTypeChanged || shouldConvertSeries) {
            const { series, labels } = this.widgetsUtilsSvc.convertDataToSeriesAndLabels(newData.data);
            this.series = this.eventsByTypeWidgetSvc.convertSeries(this.chart.type, series);
            this.labels = labels;
        }
        if (chartTypeChanged) {
            this.chartTypeChanged(newData.settings.local.chartType);
        }
        if (!this.configMode && newData.settings && newData.settings.remote) {
            // Not config mode and remote settings were changed - reload data from server
            this.loadWidgetData(this.definition);
        }
    }

    onSettingsChanged(): void {
        const settingsAreValid = this.eventsByTypeWidgetSvc.areSettingsValid(this.definitionSettings);
        this.settingsChanged.next({
            definition: this.definition,
            settings: this.definitionSettings,
            settingsAreValid: settingsAreValid
        });
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
        super.destroy();
    }

    private async getEnumTypeGlobalEventFields(): Promise<IField[]> {
        if (this.enumTypeGlobalEventFields) {
            return this.enumTypeGlobalEventFields;
        }
        // Get only these that are select or multi select
        this.globalEventFields = await this.apiSvc.getAllGlobalEventFields();
        this.enumTypeGlobalEventFields = this.azavistaSharedSvc.getSelectAndMultiSelectFields(this.globalEventFields);
        return this.enumTypeGlobalEventFields;
    }
}

